import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Link,
  Divider
} from '@mui/material';
import { Add, Edit, Delete, OpenInNew, SupportAgent } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const emptyForm = {
  name: '',
  url: '',
  description: '',
  sortOrder: 0
};

const TechnicalSupport = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/technical-support`);
      setLinks(response.data.links || []);
    } catch (error) {
      console.error('Error fetching technical support links:', error);
      showToast(error.response?.data?.message || 'Failed to load software links', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const handleOpenAdd = () => {
    setSelectedLink(null);
    setFormData(emptyForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (link) => {
    setSelectedLink(link);
    setFormData({
      name: link.name || '',
      url: link.url || '',
      description: link.description || '',
      sortOrder: link.sortOrder ?? 0
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenDelete = (link) => {
    setLinkToDelete(link);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Software name is required';
    }
    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else {
      try {
        const parsed = new URL(formData.url.trim());
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          errors.url = 'URL must start with http:// or https://';
        }
      } catch {
        errors.url = 'Enter a valid URL (include http:// or https://)';
      }
    }
    if (formData.sortOrder !== '' && Number.isNaN(Number(formData.sortOrder))) {
      errors.sortOrder = 'Sort order must be a number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      name: formData.name.trim(),
      url: formData.url.trim(),
      description: formData.description.trim(),
      sortOrder: Number(formData.sortOrder) || 0
    };

    try {
      if (selectedLink) {
        await axios.put(`${API_URL}/technical-support/${selectedLink._id}`, payload);
        showToast('Software link updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/technical-support`, payload);
        showToast('Software link added successfully', 'success');
      }
      setDialogOpen(false);
      setSelectedLink(null);
      fetchLinks();
    } catch (error) {
      console.error('Error saving software link:', error);
      const apiErrors = error.response?.data?.errors;
      if (apiErrors?.length) {
        const mapped = {};
        apiErrors.forEach((e) => {
          if (e.path) mapped[e.path] = e.msg;
        });
        setFormErrors(mapped);
      }
      showToast(error.response?.data?.message || 'Failed to save software link', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/technical-support/${linkToDelete._id}`);
      showToast('Software link removed successfully', 'success');
      setDeleteDialogOpen(false);
      setLinkToDelete(null);
      fetchLinks();
    } catch (error) {
      console.error('Error deleting software link:', error);
      showToast(error.response?.data?.message || 'Failed to remove software link', 'error');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SupportAgent color="primary" fontSize="large" />
          <Typography variant="h4">Technical Support</Typography>
        </Stack>
        {isSuperAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            Add Software Link
          </Button>
        )}
      </Stack>

      {isSuperAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          As Super Admin, you can add and manage links to external software. All users can view and open them from this page.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Typography color="text.secondary">Loading software links...</Typography>
        ) : links.length === 0 ? (
          <Typography color="text.secondary">
            No software links have been added yet.
            {isSuperAdmin ? ' Use "Add Software Link" to publish your first link.' : ''}
          </Typography>
        ) : (
          <List disablePadding>
            {links.map((link, index) => (
              <React.Fragment key={link._id}>
                {index > 0 && <Divider />}
                <ListItem
                  alignItems="flex-start"
                  sx={{ py: 2, pr: isSuperAdmin ? 14 : 2 }}
                  secondaryAction={
                    isSuperAdmin ? (
                      <Stack direction="row" spacing={0.5}>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEdit(link)}>
                          <Edit />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleOpenDelete(link)}>
                          <Delete />
                        </IconButton>
                      </Stack>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Link
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {link.name}
                        <OpenInNew fontSize="small" />
                      </Link>
                    }
                    secondary={
                      <>
                        {link.description && (
                          <Typography component="span" variant="body2" color="text.secondary" display="block">
                            {link.description}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          {link.url}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedLink ? 'Edit Software Link' : 'Add Software Link'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Software name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
              required
            />
            <TextField
              label="URL"
              value={formData.url}
              onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
              error={!!formErrors.url}
              helperText={formErrors.url || 'Full link including https://'}
              fullWidth
              required
            />
            <TextField
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Sort order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: e.target.value }))}
              error={!!formErrors.sortOrder}
              helperText={formErrors.sortOrder || 'Lower numbers appear first'}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleFormSubmit}>
            {selectedLink ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Software Link</DialogTitle>
        <DialogContent>
          <Typography>
            Remove &quot;{linkToDelete?.name}&quot;? Users will no longer see this link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicalSupport;
